import React from 'react';
import { getURLParameters } from '../../client/startup/getURLParameters';
import PropTypes from 'prop-types';
import getContext from 'react-recompose/getContext';
import { withArrowData } from './withArrowData';
import { Subscription, Scheduler } from '@reactivex/rxjs';
import shallowEqual from 'react-recompose/shallowEqual';
import { getBufferNFOs, getNumElements } from './util';
import { attachTickSink, detachTickSink } from './tickSink';
import * as legacySubjects from '@graphistry/viz/src/client/legacy';

const debug = (...args) => {};

// import { sprintf as printf } from 'sprintf-js';
// const debug = (...args) => console.log(`    StreamGL:react-renderer:`, printf(...args));

//edgesdf hover/selection overlay items use default edgeOpacity values
const EDGE_OVERLAY_ITEMS = {
    edgesdfhighlight: true,
    edgesdfselected: true,
    edgesdfarrowhighlight: true,
    edgesdfarrowselected: true
};

//edges stay visible during camera movement up to this many edges;
//above it gesture frames fall back to points-only.
//?hideFastEdges=<n> overrides (0 = always hide during movement).
const FAST_FRAME_EDGE_LIMIT = (() => {
    const { hideFastEdges } = typeof window !== 'undefined'
        ? getURLParameters(window.location.href) : {};
    return typeof hideFastEdges === 'number' ? hideFastEdges : 10000000;
})();

class Renderer extends React.Component {
    constructor(props, context) {
        super(props, context);
        this.arrowItems = {};
        this.renderFast = undefined;
        this.renderNewData = undefined;
        this.renderPanZoom = undefined;
        this.renderResized = undefined;
        this.renderMouseOver = undefined;
        this.hasRenderedSelectionOnce = false;
        this.cameraChangesSubscription = new Subscription();
        this.simulateOn = props.simulateOn || legacySubjects.simulateOn;
        this.toggleZoomIn = props.toggleZoomIn || legacySubjects.toggleZoomIn;
        this.toggleCenter = props.toggleCenter || legacySubjects.toggleCenter;
        this.toggleZoomOut = props.toggleZoomOut || legacySubjects.toggleZoomOut;
        this.cameraChanges = props.cameraChanges || legacySubjects.cameraChanges;
        this.div = (
            <div ref={(x) => (this.container = x)} style={{
                position: `absolute`,
                width: `100%`, height: `100%`,
                top: 0, left: 0, right: 0, bottom: 0,
            }}/>
        );
    }
    shouldComponentUpdate(nextProps) {

        const {
            axis: currAxes,
            camera: currCamera,
            background: currBackground,
            edges: currEdges,
            points: currPoints,
            highlight: currHighlight,
            selection: currSelection,
            globalBoundBox: currGlobalBoundBox,
            ...restCurrProps
        } = this.props;

        const {
            axis: nextAxes,
            camera: nextCamera,
            background: nextBackground,
            edges: nextEdges,
            points: nextPoints,
            highlight: nextHighlight,
            selection: nextSelection,
            globalBoundBox: nextGlobalBoundBox,
            ...restNextProps
        } = nextProps;

        return (
            !shallowEqual(currEdges, nextEdges) ||
            !shallowEqual(currPoints, nextPoints) ||
            !falcorEqualOrShallowEqual(currAxes, nextAxes) ||
            !falcorEqualOrShallowEqual(currCamera, nextCamera) ||
            !falcorEqualOrShallowEqual(currHighlight, nextHighlight) ||
            !falcorEqualOrShallowEqual(currSelection, nextSelection) ||
            !shallowEqual(restCurrProps, restNextProps)
        );
    }
    UNSAFE_componentWillMount() {
        this.updateRendererStateAndScheduler({}, this.props, this.state);
    }
    UNSAFE_componentWillUpdate(nextProps, nextState) {
        this.updateRendererStateAndScheduler(this.props, nextProps, nextState);
    }
    componentDidMount() {
        debug('mounted renderer');
        if (!this.container.contains(this.props.simulation)) {
            debug('adding the simulation canvas element');
            this.container.appendChild(this.props.simulation);
        }
        this.cameraChangesSubscription = this.cameraChanges
            .distinctUntilChanged(shallowEqual, ({ width, height, center = {} } = {}) => ({
                width, height, ...center
            }))
            .auditTime(0, Scheduler.animationFrame)
            .subscribe(() => (this.renderPanZoom = true) && this.forceUpdate());
    }
    componentWillUnmount() {
        debug('unmounting renderer');
        detachTickSink();
        const { cameraChangesSubscription } = this;
        cameraChangesSubscription.unsubscribe();
        this.div = undefined;
        this.container = undefined;
        this.arrowItems = undefined;
        this.renderFast = undefined;
        this.simulateOn = undefined;
        this.toggleZoomIn = undefined;
        this.toggleCenter = undefined;
        this.toggleZoomOut = undefined;
        this.cameraChanges = undefined;
        this.renderNewData = undefined;
        this.renderPanZoom = undefined;
        this.renderResized = undefined;
        this.renderMouseOver = undefined;
        this.cameraChangesSubscription = undefined;
    }
    render() {
        let { renderFast, renderNewData, renderPanZoom, renderMouseOver, renderResized } = this;
        const { renderState, renderingScheduler, simulation, highlight, selection } = this.props;
        if (!(renderState && renderingScheduler && simulation && this.container)) return this.div;
        let trigger = (renderFast || this.props.simulating) ? 'renderSceneFast' : 'renderSceneFull';
        if (renderNewData || renderPanZoom) {
            this.renderNewData = false;
            this.renderPanZoom = false;
            if (renderPanZoom) {
                debug(`renderPanZoom with trigger "${trigger}"`);
                renderingScheduler.renderScene('panzoom', { trigger });
            } else if (renderNewData) {
                debug(`renderNewData with trigger "${trigger}"`);
                renderingScheduler.renderScene('vboupdate', { trigger });
                !(renderFast || this.props.simulating) &&
                    renderingScheduler.renderScene('vboupdate_picking', { items: ['pointsampling'] });
            }
            (typeof this.renderFast !== 'number') && (this.renderFast = undefined);
            return this.div;
        }
        if (renderMouseOver && (trigger = 'mouseOverEdgeHighlight')) {
            this.renderMouseOver = false;
            debug(`renderMouseOver with trigger "${trigger}"`);
            let darken = highlight.darken || false;
            let selectionEdges = selection.edge || [];
            let highlightEdges = highlight.edge || [];
            let selectionPoints = selection.point || [];
            let highlightPoints = highlight.point || [];
            if (darken && !selectionEdges.length && !selectionPoints.length) {
                selectionEdges = highlightEdges;
                selectionPoints = highlightPoints;
                highlightEdges = highlightPoints = undefined;
            }
            const mouseoverTask = {
                trigger,
                data: {
                    darken,
                    neighborhoodHighlight: this.props.points ? this.props.points.neighborhoodHighlight : "both",
                    neighborhoodHighlightHops: this.props.points ? this.props.points.neighborhoodHighlightHops : 1,
                    selected: { edgeIndices: selectionEdges, nodeIndices: selectionPoints, },
                    highlight: { edgeIndices: highlightEdges, nodeIndices: highlightPoints, },
                }
            };
            if (!this.hasRenderedSelectionOnce) {
                this.hasRenderedSelectionOnce = true;
                // trick the rendering scheduler into believing
                // it _really_ wants to render the first mouseover task
                renderingScheduler.lastMouseoverTask = mouseoverTask;
            }
            renderingScheduler.renderScene('mouseOver', mouseoverTask);
            return this.div;
        }
        if (renderResized) {
            this.renderResized = false;
            debug(`renderResized`);
            renderingScheduler.renderScene('resizeRerender', { trigger: 'picking' });
            renderingScheduler.renderScene('resizeRerender', { trigger: 'highlight' });
            if (renderingScheduler.lastMouseoverTask) {
                renderingScheduler.renderScene('mouseOver', renderingScheduler.lastMouseoverTask);
            }
            return this.div;
        }
        return this.div;
    }
    updateRendererStateAndScheduler(currProps = {}, nextProps = {}, nextState = {}) {
        const { renderState, renderingScheduler } = nextProps;
        const { renderer } = renderingScheduler || {};

        if (!renderer || !renderState || !renderingScheduler) {
            return;
        }

        //keep the tick sink's context current (the scheduler and its
        //renderState are recreated on scene changes); worker tick batches
        //bypass React through it. Runs from the constructor and every
        //willUpdate -- same coverage render() had.
        attachTickSink({ renderingScheduler, $falcor: nextProps.$falcor });

        const {
            axis: currAxes,
            background: currBackground,
            edges: currEdges = {},
            camera: currCamera = {},
            points: currPoints = {},
            highlight: currHighlight = {},
            selection: currSelection = {},
            simulating: currSimulating = true,
            showArrows: currShowArrows = true,
            neighborhoodHighlight: currNeighborhoodHighlight = 'both',
            neighborhoodHighlightHops: currNeighborhoodHighlightHops = 1,
            sceneWidth: currSceneWidth = 0,
            sceneHeight: currSceneHeight = 0,
            simulationWidth: currSimWidth = 0,
            simulationHeight: currSimHeight = 0,
            nodesUpdate: currNodesUpdate,
            edgesUpdate: currEdgesUpdate,
            globalBoundBox: currGlobalBoundBox,
            nodesTableLength: currNodesTableLength,
            edgesTableLength: currEdgesTableLength,
        } = currProps;

        const {
            $falcor,
            axis: nextAxes,
            background: nextBackground = currBackground,
            edges: nextEdges = currEdges,
            camera: nextCamera = currCamera,
            points: nextPoints = currPoints,
            highlight: nextHighlight = currHighlight,
            selection: nextSelection = currSelection,
            simulating: nextSimulating = currSimulating,
            showArrows: nextShowArrows = currShowArrows,
            neighborhoodHighlight: nextNeighborhoodHighlight = currNeighborhoodHighlight,
            neighborhoodHighlightHops: nextNeighborhoodHighlightHops = currNeighborhoodHighlightHops,
            sceneWidth: nextSceneWidth = currSceneWidth,
            sceneHeight: nextSceneHeight = currSceneHeight,
            simulationWidth: nextSimWidth = currSimWidth,
            simulationHeight: nextSimHeight = currSimHeight,
            nodesUpdate: nextNodesUpdate = currNodesUpdate,
            edgesUpdate: nextEdgesUpdate = currEdgesUpdate,
            globalBoundBox: nextGlobalBoundBox = currGlobalBoundBox,
            nodesTableLength: nextNodesTableLength = currNodesTableLength,
            edgesTableLength: nextEdgesTableLength = currEdgesTableLength,
        } = nextProps;

        let {
            renderFast,
            renderNewData,
            renderResized,
            renderPanZoom,
            renderMouseOver,
            hasRenderedSelectionOnce
        } = this;

        const updateArg = {
            $falcor,
            currAxes, nextAxes,
            currBackground, nextBackground,
            currEdges, currPoints,
            nextEdges, nextPoints,
            currCamera, nextCamera,
            currSceneWidth, currSceneHeight,
            nextSceneWidth, nextSceneHeight,
            currSimWidth, currSimHeight,
            nextSimWidth, nextSimHeight,
            currHighlight, nextHighlight,
            currSelection, nextSelection,
            currShowArrows, nextShowArrows,
            currNeighborhoodHighlight, nextNeighborhoodHighlight,
            currNeighborhoodHighlightHops, nextNeighborhoodHighlightHops,
            currSimulating, nextSimulating,
            currNodesUpdate, nextNodesUpdate,
            currEdgesUpdate, nextEdgesUpdate,
            nextGlobalBoundBox, currGlobalBoundBox,
            currNodesTableLength, nextNodesTableLength,
            currEdgesTableLength, nextEdgesTableLength,
            renderer, renderState, renderingScheduler,
        };

        let renderFullNOW = nextEdges.elements <= FAST_FRAME_EDGE_LIMIT;

        renderNewData = this.updateWebGLBuffers(updateArg) || renderNewData;
        renderPanZoom = this.updateRadialAxes(updateArg) || renderPanZoom;
        renderPanZoom = this.updateNumElements(updateArg) || renderPanZoom;
        renderPanZoom = this.updateShowArrows(updateArg) || renderPanZoom;
        renderPanZoom = this.updateEdgeScaling(updateArg) || renderPanZoom;
        renderPanZoom = this.updateEdgeCurvature(updateArg) || renderPanZoom;
        renderPanZoom = this.updatePointScaling(updateArg) || renderPanZoom;
        renderPanZoom = this.updateEdgeOpacity(updateArg) || renderPanZoom;
        renderPanZoom = this.updatePointOpacity(updateArg) || renderPanZoom;
        renderPanZoom = this.updatePointStroke(updateArg) || renderPanZoom;
        renderPanZoom = this.updateCameraCenterAndZoom(updateArg) || renderPanZoom;
        renderPanZoom = this.updateSimulating(updateArg) || (renderPanZoom && !nextSimulating);
        renderMouseOver = this.updateSceneHighlight(updateArg) || (renderMouseOver && !nextSimulating);
        renderResized = this.updateSceneDimensions(updateArg) && !renderMouseOver && !nextSimulating && !renderPanZoom;

        if (hasRenderedSelectionOnce === true) {
            renderMouseOver = this.updateSceneSelection(updateArg) || (renderMouseOver && !nextSimulating);
        } else {
            const { edge: nextEdge, point: nextPoint } = nextSelection;
            renderMouseOver = (nextEdge && nextEdge.length) || (nextPoint && nextPoint.length) || renderMouseOver;
        }

        if (renderNewData || renderPanZoom || renderResized) {
            if (typeof renderFast === 'number') {
                clearTimeout(renderFast);
                renderFast = undefined;
                debug('clearing renderFast');
            }
            if (renderFullNOW) {
                renderFast = false;
                renderPanZoom = true;
                debug('requesting renderFull', renderFullNOW);
            } else {
                debug('requesting renderFast', renderFast);
                if (typeof renderFast === 'undefined') {
                    debug('enqueueing renderFast');
                    renderFast = setTimeout(() => {
                        debug('executing renderFast');
                        this.renderFast = false;
                        this.renderPanZoom = true;
                        this.forceUpdate();
                    }, 200);
                }
            }
        }

        renderNewData   ? debug(`update: renderNewData %t`, renderNewData) :
        renderPanZoom   ? debug(`update: renderPanZoom %t`, renderPanZoom) :
        renderResized   ? debug(`update: renderResized %t`, renderResized) :
        renderMouseOver ? debug(`update: renderMouseOver %t`, renderMouseOver) :
        renderFast      ? debug(`update: renderFast %t`, renderFast) :
                          debug(`update: nothing to render`);

        this.renderFast = renderFast;
        this.renderNewData = renderNewData;
        this.renderPanZoom = renderPanZoom;
        this.renderResized = renderResized;
        this.renderMouseOver = renderMouseOver;
    }
    updateSceneDimensions({ currSimWidth, currSimHeight, nextSimWidth, nextSimHeight }) {
        return currSimWidth !== nextSimWidth || currSimHeight !== nextSimHeight;
    }
    updateWebGLBuffers({
        $falcor,
        currNodesUpdate, nextNodesUpdate,
        currEdgesUpdate, nextEdgesUpdate,
        currNodesTableLength, nextNodesTableLength,
        currEdgesTableLength, nextEdgesTableLength,
        renderer, renderState, renderingScheduler,
    }) {
        debug('maybe updating webgl buffers');

        let rendererModel = {};
        let buffers = {}, lengths = [], shouldUpdate = false;

        const graphUpdates = [
            ['node', currNodesUpdate, nextNodesUpdate, currNodesTableLength, nextNodesTableLength],
            ['edge', currEdgesUpdate, nextEdgesUpdate, currEdgesTableLength, nextEdgesTableLength]
        ];
        for (const [name, curr, next, currLength, nextLength] of graphUpdates) {
            let item = null, len;
            if (item !== null) {}
            else if (curr && !curr.loaded && (item = curr)) { len = currLength; }
            else if (next && !next.loaded && (item = next)) { len = nextLength; }
            if (item) {
                buffers = { ...buffers, ...getBufferNFOs(item, !item.table, name) };
                lengths = [...lengths, ...getNumElements(renderState.config, name, len)];
                item.loaded = shouldUpdate = lengths.length > 0;
                rendererModel[name === 'node' ? 'points' : 'edges'] = { elements: len };
            }
        }
        
        if (shouldUpdate) {
            $falcor.withoutDataSource().set({ json: rendererModel }).subscribe();
            debug([`updated buffers:`, ...Object.keys(buffers)].join('\n'));
            debug([`new numElements:`, ...lengths.map((x) => x.join(': '))].join('\n'));
            renderer.loadBuffers(renderState, buffers);
            lengths.forEach(([name, numE]) => renderer.setNumElements(renderState, name, numE));
            renderingScheduler.appSnapshot.vboUpdated = true;
            Object.keys(buffers).forEach((name) => {
                if (name in renderingScheduler.appSnapshot.buffers) {
                    renderingScheduler.appSnapshot.buffers[name] = renderState.hostBuffersCache[name];
                }
                if ((name in renderingScheduler.appSnapshot.bufferReceivedVersions)) {
                    renderingScheduler.appSnapshot.bufferReceivedVersions[name] =
                        (renderingScheduler.appSnapshot.bufferReceivedVersions[name] || 0) + 1;
                }
            });
            return true;
        }
        return false;
    }
    updateRadialAxes({ currBackground, nextBackground, currAxes, nextAxes, renderingScheduler }) {
        if (!shallowEqual(currAxes, nextAxes) || !shallowEqual(currBackground, nextBackground)) {
            renderingScheduler.loadRadialAxes(nextAxes, nextBackground);
            return true;
        }
        return false;
    }
    updateNumElements({ currEdges, currPoints, nextEdges, nextPoints, renderState, renderingScheduler }) {
        if (currEdges.elements !== nextEdges.elements || currPoints.elements !== nextPoints.elements) {
            renderingScheduler.attemptToAllocateBuffersOnHints(renderState.config, renderState, {
                edges: nextEdges.elements,
                points: nextPoints.elements
            });
            return true;
        }
        return false;
    }
    updateSimulating({ currSimulating, nextSimulating }) {
        if (currSimulating !== nextSimulating) {
            this.simulateOn.next(nextSimulating);
            return true;
        }
        return false;
    }
    updateEdgeScaling({ currEdges, nextEdges, renderState }) {
        if (currEdges.scaling !== nextEdges.scaling) {
            renderState.camera.setEdgeScaling(nextEdges.scaling);
            return true;
        }
        return false;
    }
    updateEdgeCurvature({ currEdges, nextEdges, renderingScheduler }) {
        if (currEdges.curvature !== nextEdges.curvature) {
            renderingScheduler.lastEdgesData.curvatureChanged = true;
            renderingScheduler.lastEdgesData.curvature = nextEdges.curvature;
            return true;
        }
        return false;
    }
    updatePointScaling({ currPoints, nextPoints, renderState }) {
        if (currPoints.scaling !== nextPoints.scaling) {
            renderState.camera.setPointScaling(nextPoints.scaling);
            return true;
        }
        return false;
    }
    updateEdgeOpacity({ currEdges, nextEdges, renderState }) {
        if (currEdges.opacity !== nextEdges.opacity) {
            const { uniforms } = renderState;
            const opacity = [nextEdges.opacity];
            for (const uniformName in uniforms) {
                if (EDGE_OVERLAY_ITEMS[uniformName]) {
                    continue;
                }
                const uniform = uniforms[uniformName];
                if ('edgeOpacity' in uniform) {
                    uniform['edgeOpacity'] = opacity;
                }
            }
            return true;
        }
        return false;
    }
    updatePointOpacity({ currPoints, nextPoints, renderState }) {
        if (currPoints.opacity !== nextPoints.opacity) {
            const { uniforms } = renderState;
            const opacity = [nextPoints.opacity];
            for (const uniformName in uniforms) {
                const uniform = uniforms[uniformName];
                if ('pointOpacity' in uniform) {
                    uniform['pointOpacity'] = opacity;
                }
            }
            return true;
        }
        return false;
    }
    updatePointStroke({ currPoints, nextPoints, renderState }) {
        if (currPoints.stroke !== nextPoints.stroke) {
            const { uniforms } = renderState;
            const stroke = [Math.abs(nextPoints.stroke)];
            const strokeNeg = [-stroke[0]]
            const strokeUniformMap = { // key -> program name, value -> is stroke value positive
                pointculled: false,
                pointculledoutline: true,
                pointhighlight: false,
                pointhighlightoutline: true,
                pointselected: false,
                pointselectedoutline: true
            };
            for (const program in strokeUniformMap) {
                uniforms[program].stroke = strokeUniformMap[program] ? stroke : strokeNeg;
            }
            return true;
        }
        return false;
    }
    updateShowArrows({ currShowArrows, nextShowArrows, renderState }) {
        if (currShowArrows !== nextShowArrows) {
            const { arrowItems } = this;
            const { items: rendererItems } = renderState;
            const addToMap = nextShowArrows ? rendererItems : arrowItems;
            const deleteFromMap = nextShowArrows ? arrowItems : rendererItems;
            ['arrowculled', 'edgesdfarrow', 'arrowhighlight', 'edgesdfarrowhighlight',
             'arrowselected', 'edgesdfarrowselected'].forEach(itemName => {
                if (deleteFromMap[itemName]) {
                    addToMap[itemName] = deleteFromMap[itemName];
                    delete deleteFromMap[itemName];
                }
            });
            ['edgesdf', 'edgesdfhighlight', 'edgesdfselected'].forEach(itemName => {
                const uniformsMap = renderState.uniforms[itemName];
                if (uniformsMap && uniformsMap.showArrows) {
                    uniformsMap.showArrows = [nextShowArrows ? 1 : 0];
                }
            });
            return true;
        }
        return false;
    }
    updateCameraCenterAndZoom({
        renderState,
        currCamera, nextCamera,
        nextSimWidth, nextSimHeight,
        nextSceneWidth, nextSceneHeight,
        nextGlobalBoundBox, currGlobalBoundBox
    }) {
        const { center: currCenter = {} } = currCamera || {};
        const { center: nextCenter = currCenter } = nextCamera || {};
        const firstBBox = !currGlobalBoundBox && nextGlobalBoundBox;
        if (!firstBBox && !nextCenter.auto && falcorEqualOrShallowEqual(currCamera, nextCamera)) {
            return false;
        }
        if (firstBBox || nextCenter.auto || !falcorEqualOrShallowEqual(currCenter, nextCenter)) {
            if (nextGlobalBoundBox && nextCenter.auto) {
                const { camera } = renderState;
                const xRatio = (nextSimWidth / nextSceneWidth) || 1;
                const yRatio = (nextSimHeight / nextSceneHeight) || 1;
                const [top, right, bottom, left] = nextGlobalBoundBox;
                const isAlreadyCentered = camera.isCenteredOn(left, right, top, bottom, xRatio, yRatio);
                !isAlreadyCentered && this.toggleCenter.next({ boundBox: nextGlobalBoundBox, xRatio, yRatio });
                return !isAlreadyCentered;
            }
            return firstBBox || !shallowEqual(currCenter, nextCenter);
        } else if (nextCamera.zoom < currCamera.zoom) {
            return this.toggleZoomIn.next() || true;
        } else if (nextCamera.zoom > currCamera.zoom) {
            return this.toggleZoomOut.next() || true;
        }
        return false;
    }
    updateSceneHighlight({
        currHighlight, nextHighlight,
        currNeighborhoodHighlight, nextNeighborhoodHighlight,
        currNeighborhoodHighlightHops, nextNeighborhoodHighlightHops
    }) {
        const { edge: currEdge, point: currPoint } = currHighlight;
        const { edge: nextEdge, point: nextPoint } = nextHighlight;
        if (!currEdge || !currPoint) {
            return !!(nextEdge || nextPoint);
        }
        return (
            !falcorEqualOrShallowEqual(currHighlight, nextHighlight) 
            || (currNeighborhoodHighlight !== nextNeighborhoodHighlight)
            || (currNeighborhoodHighlightHops !== nextNeighborhoodHighlightHops)
        );
    }
    updateSceneSelection({ currSelection, nextSelection }) {
        const { edge: currEdge, point: currPoint } = currSelection;
        const { edge: nextEdge, point: nextPoint } = nextSelection;
        if (!currEdge || !currPoint) {
            return !!(nextEdge || nextPoint);
        }
        return !falcorEqualOrShallowEqual(currSelection, nextSelection);
    }
}

const RendererWrapped = getContext({
    play: PropTypes.number,
    dataset: PropTypes.string,
    session: PropTypes.string,
    simulation: PropTypes.object,
    renderState: PropTypes.object,
    renderingScheduler: PropTypes.object
})(withArrowData(Renderer));

export { RendererWrapped as Renderer };

function falcorEqualOrShallowEqual(a, b) {
    // check for falcor version number
    if (a && b && a.$__version !== undefined && b.$__version !== undefined) {
        return a.$__version === b.$__version;
    }
    // otherwise fallback to shallow equal
    return shallowEqual(a, b);
}
